import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { DEAL_STAGES, type StageMeta } from '../constants';
import type { BadgeTone } from '@/shared/components/ui/Badge';

interface MasterItem {
  _id: string;
  name: string;
  code: string;
  sortOrder: number;
  isActive: boolean;
  meta?: { isClosed?: boolean };
}

/** Tone assignment heuristic based on position and closed status. */
function inferTone(item: MasterItem, index: number, total: number): BadgeTone {
  if (item.meta?.isClosed) {
    return item.code === 'closed_won' ? 'success' : 'danger';
  }
  const pct = index / total;
  if (pct < 0.2) return 'neutral';
  if (pct < 0.4) return 'info';
  if (pct < 0.6) return 'primary';
  return 'warning';
}

/**
 * Fetches deal stages from the stage master. Falls back to the hardcoded
 * DEAL_STAGES constant if the master has no entries (first run before seeding).
 */
export function useDealStages() {
  return useQuery({
    queryKey: ['masters', 'stage'],
    queryFn: async () => {
      const { data } = await api.get<{ items: MasterItem[] }>('/api/masters/stage');
      const items = (data.items ?? []).filter((i) => i.isActive);
      if (items.length === 0) return DEAL_STAGES;

      return items.map((item, idx): StageMeta => {
        // Check if there's a matching entry in the static fallback for tone
        const staticMatch = DEAL_STAGES.find((s) => s.key === item.code);
        return {
          key: item.code || item.name.toLowerCase().replace(/\s+/g, '_'),
          label: item.name,
          tone: staticMatch?.tone ?? inferTone(item, idx, items.length),
        };
      });
    },
    staleTime: 10 * 60 * 1000, // stages rarely change
  });
}
